<template>
  <span class="file-input">
    <div
      ref="editable"
      class="file-input__text-field"
      :class="[
        `file-input-${type}`,
        { 'file-input__text-field--invalid': showInvalidState && !isValid },
        { 'file-input__text-field--disabled': disabled || blocked },
      ]"
      :contenteditable="
        !disabled && (isValid && !newEl ? isContentEditable : true)
      "
      @focus="focus"
      @blur="onBlur"
      @keydown.enter.prevent="onPressEnter"
      @keydown.esc="onPressEsc"
      @keyup.ctrl.backspace="clear"
      @input="onInput"
      @click="onClick"
    >
      {{ displayText }}
    </div>
    <app-tooltip
      v-if="showInvalidState && !isValid"
      :text="invalidHint"
      position="top"
    >
      <template #activator>
        <span v-if="!isValid" class="file-input__icon">
          <app-icon
            name="invalid"
            width="16px"
            color="#FF5533"
            cursor="pointer"
          />
        </span>
      </template>
    </app-tooltip>
    <app-tooltip
      v-if="isFocused && normalizedValue.length > 0"
      :text="$t('clear_field_hotkey')"
      ><template #activator>
        <span @click="clear" @mousedown.prevent>
          <app-icon
            name="arrow-left"
            width="16px"
            color="#979797"
            cursor="pointer"
          />
        </span>
      </template>
    </app-tooltip>
  </span>
</template>

<script setup lang="ts">
import { i18n } from "@/i18n";
import { ref, watch, computed, onMounted, nextTick } from "vue";
import AppIcon from "../ui-kit/icon/AppIcon.vue";
import AppTooltip from "../ui-kit/tooltip/AppTooltip.vue";

interface Props {
  modelValue: string;
  type?: string;
  newEl?: boolean;
  disabled?: boolean;
  blocked?: boolean;
  invalidValues?: string[];
  showInvalidState?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  type: "folder",
  disabled: false,
  blocked: false,
  invalidValues: () => [],
  showInvalidState: true,
});

const emit = defineEmits(["update:modelValue", "clear", "enter", "blur"]);

const originalText = ref(props.modelValue || "");
const innerText = ref(props.modelValue || "");
const editable = ref<HTMLElement | null>(null);
const isFocused = ref(false);
const isContentEditable = ref(false);
const isEditing = ref(false);
const isEnter = ref(false);

const normalizeComparedValue = (value: string) =>
  value.trim().toLowerCase();

const comparedValue = computed(() => {
  if (props.type !== "file" || !isEditing.value) return innerText.value;

  return getResultingFileName(innerText.value);
});

const normalizedValue = computed(() =>
  normalizeComparedValue(comparedValue.value),
);
const isDuplicate = computed(() =>
  props.invalidValues.some(
    (value) => normalizeComparedValue(value) === normalizedValue.value,
  ),
);
const isValid = computed(
  () => innerText.value.trim().length > 0 && !isDuplicate.value,
);
const invalidHint = computed(() =>
  isDuplicate.value
    ? String(i18n.global.t('element_name_exists'))
    : String(i18n.global.t('field_required')),
);

const displayText = computed(() => innerText.value);

const onClick = (e: MouseEvent) => {
  if (isContentEditable.value) {
    e.stopPropagation();
  }
};

function removeExtension(filename: string): string {
  if (!filename) return "";
  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1) return filename;
  return filename.substring(0, lastDotIndex);
}

function getExtension(filename: string): string {
  if (!filename) return "";
  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1) return "";
  return filename.substring(lastDotIndex);
}

function getResultingFileName(value: string): string {
  const extension = getExtension(originalText.value);
  return value + extension;
}

const clear = (e: MouseEvent) => {
  e.stopPropagation();

  innerText.value = "";

  updateEditable();

  nextTick(() => {
    if (editable.value) {
      focus();
    }
  });
};

const onPressEnter = () => {
  if (isValid.value) {
    saveChanges();
  }
};

const onPressEsc = () => {
  cancelEditing();
};

const onBlur = () => {
  if (!isEditing.value) {
    emit("blur");
    return;
  }

  if (isValid.value) {
    saveChanges();
    emit("blur");
    return;
  }

  nextTick(() => {
    editable.value?.focus();
    isFocused.value = true;
    isContentEditable.value = true;
  });
};

const saveChanges = () => {
  isEditing.value = false;

  if (originalText.value && props.type === "file") {
    const currentText = editable.value?.innerText || innerText.value;
    innerText.value = getResultingFileName(currentText);
  }

  emit("update:modelValue", innerText.value);
  emit("enter", innerText.value);
  blur();
};

const cancelEditing = () => {
  isEditing.value = false;
  innerText.value = props.modelValue;

  updateEditable();
  blur();
};

const focus = () => {
  if (props.disabled) return;

  isContentEditable.value = true;
  if (!isEditing.value) {
    originalText.value = innerText.value;
    isEditing.value = true;
    if (props.type === "file") {
      innerText.value = removeExtension(originalText.value);
    }
  }

  nextTick(() => {
    if (editable.value) {
      editable.value.focus();
      isFocused.value = true;

      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(editable.value);
      if (!props.newEl) {
        range.collapse(false);
      }
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  });
};

const blur = () => {
  if (editable.value) {
    editable.value.blur();
    isFocused.value = false;
    isContentEditable.value = false;
  }
};

defineExpose({
  focus,
  blur,
  finishEditing: onBlur,
});

watch(
  () => props.modelValue,
  (v) => {
    if (v !== innerText.value) {
      innerText.value = v || "";
      originalText.value = v || "";
      updateEditable();
    }
  },
);

watch(innerText, (v) => {
  if (!isEditing.value) {
    emit("update:modelValue", v);
  }
});

function setEditableText(text: string) {
  if (!editable.value) return;
  editable.value.innerText = text;
}

function updateEditable() {
  setEditableText(innerText.value);
}

function onInput(e: Event) {
  const el = editable.value;
  if (!el) return;

  const textWithoutExtension = el.innerText || "";
  innerText.value = textWithoutExtension;

  const caret = saveCaretPosition(el);

  nextTick(() => {
    restoreCaretPosition(el, caret);
  });
}

function saveCaretPosition(el: HTMLElement) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  const preRange = range.cloneRange();

  preRange.selectNodeContents(el);
  preRange.setEnd(range.endContainer, range.endOffset);

  return preRange.toString().length;
}

function restoreCaretPosition(el: HTMLElement, caretPos: number) {
  if (caretPos === null) return;

  const nodeStack = [el];
  let charCount = 0;
  let node: Node | undefined;

  while ((node = nodeStack.pop())) {
    if (node.nodeType === Node.TEXT_NODE) {
      const textLength = (node.textContent || "").length;

      if (charCount + textLength >= caretPos) {
        const selection = window.getSelection();
        const range = document.createRange();
        range.setStart(node, caretPos - charCount);
        range.collapse(true);
        selection?.removeAllRanges();
        selection?.addRange(range);
        return;
      }
      charCount += textLength;
    } else {
      let i = node.childNodes.length;
      while (i--) nodeStack.push(node.childNodes[i]);
    }
  }
}

onMounted(() => {
  originalText.value = props.modelValue || "";
  updateEditable();
});
</script>

<style lang="scss" scoped>
@import "@/styles/color.scss";

.file-input {
  position: relative;

  &__text-field {
    display: inline;
    overflow-wrap: anywhere;
    position: relative;
    text-align: justify;
    font-size: 14px;
    line-height: 18px;
    gap: 4px;
    min-width: 4px;
    color: $color-text_main;

    &:focus {
      border-bottom: 1px solid $color-accent;
    }

    &--invalid {
      position: relative;
      display: inline-block;
      border-bottom: 1px solid $color-urgent !important;
    }

    &--disabled {
      color: #d2d2d2;
    }
  }

  &-file {
    font-weight: 400;
  }

  &-folder {
    font-weight: 500;
  }

  &__icon {
    // padding-bottom: 1px;
    padding-left: 2px;
    // border-bottom: 1px solid $color-urgent !important;
  }
}
</style>
